// FT-116: чистая логика вкладки «Брони» карточки авто — сборка параметров серверного
// запроса и арифметика кеша загруженных месяцев календаря.
//
// Вынесено из CarCard.vue по прецеденту editSummary.js/editPanels.js: компонент — 9944
// строки, и логику, которую нужно проверять, туда класть нельзя. Здесь только чистые
// функции, без axios и без обращения к this.

const DEFAULT_PER_PAGE = 20;

// Параметры серверной таблицы из options v-data-table.
//
// Вариант «все строки» (-1) в футере убран, но значение может прийти из сохранённого
// состояния таблицы, поэтому оно приводится к дефолту и здесь: иначе один клик вернул бы
// исходную проблему — выгрузку всей истории броней (FM-03).
export function buildTableParams(options = {}, search = null) {
  const perPage = Number(options.itemsPerPage);
  const page = Number(options.page);

  const params = {
    page: Number.isFinite(page) && page > 0 ? page : 1,
    per_page: Number.isFinite(perPage) && perPage > 0 ? perPage : DEFAULT_PER_PAGE,
  };

  // Колонка не выбрана — параметры сортировки не шлём вовсе, чтобы сервер применил свой
  // дефолт (end_date по убыванию), а не получил пустую строку.
  const sortBy = Array.isArray(options.sortBy) ? options.sortBy[0] : options.sortBy;
  if (sortBy) {
    params.sort_by = sortBy;
    const desc = Array.isArray(options.sortDesc) ? options.sortDesc[0] : options.sortDesc;
    params.direction = desc ? "desc" : "asc";
  }

  const query = typeof search === "string" ? search.trim() : "";
  if (query) params.search = query;

  return params;
}

// v-calendar отдаёт видимые страницы объектами { month, year } (month — 1..12).
// Диапазон получается включительным по месяцам: с первого дня первой страницы по последний
// день последней.
export function calendarRangeFromPages(fromPage, toPage) {
  const start = fromPage || toPage;
  if (!start) return null;
  const end = toPage || fromPage;

  const range = {
    from: isoDate(start.year, start.month, 1),
    to: isoDate(end.year, end.month, daysInMonth(end.year, end.month)),
  };

  // v-calendar шлёт update:from-page и update:to-page по отдельности, и между ними состояние
  // рассогласовано: при листании вперёд from уже декабрь, а to ещё ноябрь. Такую пару
  // отдавать нельзя — сервер ответит 422, и пользователь увидит ошибку на ровном месте.
  return range.from > range.to ? null : range;
}

// Что из запрошенного диапазона ещё не загружено. Возвращает список кусков (их может быть
// два, если загруженный интервал лежит внутри запроса) либо пустой массив.
export function missingRanges(loaded, requested) {
  if (!requested) return [];

  let gaps = [{ from: requested.from, to: requested.to }];

  sortRanges(loaded).forEach((range) => {
    gaps = gaps.flatMap((gap) => subtract(gap, range));
  });

  return gaps;
}

// Добавляет загруженный кусок к кешу, сливая соседние и пересекающиеся интервалы.
//
// Добавлять сюда нужно ФАКТИЧЕСКИЙ диапазон из ответа сервера: он капит ширину 24 месяцами
// и возвращает свои from/to. Если пометить загруженным запрошенное, обрезанные месяцы
// никогда не догрузятся и останутся пустыми навсегда (CTR-02, FM-05).
export function mergeLoadedRange(loaded, added) {
  if (!added) return loaded;

  const merged = [];
  let current = { from: added.from, to: added.to };

  sortRanges([...loaded, current]).forEach((range) => {
    const last = merged[merged.length - 1];
    // Соседние (следующий начинается на другой день после конца предыдущего) тоже сливаем:
    // иначе кеш дробится на куски и лишний запрос уходит на стык.
    if (last && range.from <= nextDay(last.to)) {
      if (range.to > last.to) last.to = range.to;
    } else {
      merged.push({ from: range.from, to: range.to });
    }
  });

  return merged;
}

// Убирает интервал из набора. Нужен для учёта «летящих» запросов: диапазон помечается
// занятым при отправке и освобождается по завершении — иначе параллельные вызовы шлют
// дубликаты, а при ошибке месяц навсегда остался бы помеченным.
export function subtractRange(ranges, removed) {
  if (!removed) return ranges;
  return ranges.flatMap((range) => subtract(range, removed));
}

function subtract(gap, range) {
  if (range.to < gap.from || range.from > gap.to) return [gap];

  const parts = [];
  if (range.from > gap.from) parts.push({ from: gap.from, to: prevDay(range.from) });
  if (range.to < gap.to) parts.push({ from: nextDay(range.to), to: gap.to });
  return parts;
}

function sortRanges(ranges) {
  return [...ranges].sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
}

// Даты — строки YYYY-MM-DD, поэтому сравнение лексикографическое, а сдвиг на день считается
// через UTC: локальный пояс браузера здесь не при чём, границы диапазона живут в настенном
// фрейме компании.
function shiftDay(iso, delta) {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + delta);
  return date.toISOString().slice(0, 10);
}

function nextDay(iso) {
  return shiftDay(iso, 1);
}

function prevDay(iso) {
  return shiftDay(iso, -1);
}

function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function isoDate(year, month, day) {
  return `${year}-${pad(month)}-${pad(day)}`;
}

function pad(value) {
  return String(value).padStart(2, "0");
}
